/**
 * Base URL for OpenAI API.
 * @constant {string}
 */
import { callAPI } from '../../../Utils/Api/Api';
const APP_BASE_API_URL = `${process.env.APP_BASE_API_URL}`;

/**
 * Lists assistants.
 * @async
 * @param {number} [limit=20] - The maximum number of items to return.
 * @param {string} [order='desc'] - The sort order ('asc' or 'desc').
 * @param {number} [offset=1] - The offset for pagination.
 * @returns {Promise<Object>} The response object.
 * @throws {Error} If an error occurs while listing assistants.
 */
const listChatSessions = async (limit = -1, order = 'desc', offset = 1) => {
  try {
    const params = new URLSearchParams({
      limit: limit.limit ?? limit,
      order,
      offset: limit.offset ?? offset,
    });
    const response = await callAPI(
      `${APP_BASE_API_URL}/chatsessions?${params.toString()}`,
    );
    return {
      data: response?.payload?.data,
      TotalCount: response?.totalCount,
      isSuccess: response?.isSuccess,
      message: response?.message,
    };
  } catch (error) {
    throw new Error(`Error listing assistants: ${error.message}`);
  }
};


/**
 * Lists assistants.
 * @async
 * @returns {Promise<Object>} The response object.
 * @throws {Error} If an error occurs while listing assistants.
 * @param id
 */
const fetchChatSession = async (id) => {
  try {
    const response = await callAPI(
      `${APP_BASE_API_URL}/chatsessions/${id}`,
      'GET'
    );
    return {
      data: response?.payload,
      isSuccess: response?.isSuccess,
      message: response?.message,
    };
  } catch (error) {
    throw new Error(`Error listing assistants: ${error.message}`);
  }
};

/**
 * Creates a new assistant.
 * @async
 * @param {Object} data - Data for creating the assistant.
 * @returns {Promise<Object>} The response object.
 * @throws {Error} If an error occurs while creating the assistant.
 */
const createChatSession = async (data) => {
  try {
    const response = await callAPI(
      `${APP_BASE_API_URL}/chatsessions`,
      'POST',
      data,
    );
    return {
      data: response?.payload,
      TotalCount: response?.totalCount,
      isSuccess: response?.isSuccess,
      message: response?.message,
    };
  } catch (error) {
    throw new Error(`Error creating assistant: ${error.message}`);
  }
};

/**
 * Creates a new assistant.
 * @async
 * @param {Object} data - Data for creating the assistant.
 * @param id
 * @returns {Promise<Object>} The response object.
 * @throws {Error} If an error occurs while creating the assistant.
 */
const updateChatSession = async (data, id) => {
  try {
    const response = await callAPI(
      `${APP_BASE_API_URL}/chatsessions/${id}`,
      'POST',
      data,
    );
    return {
      data: response?.payload?.data,
      TotalCount: response?.totalCount,
      isSuccess: response?.isSuccess,
      message: response?.message,
    };
  } catch (error) {
    throw new Error(`Error creating assistant: ${error.message}`);
  }
};
/**
 * Deletes an existing assistant.
 * @async
 * @param {string} assistantId - The ID of the assistant.
 * @returns {Promise<Object>} The response object.
 * @throws {Error} If an error occurs while deleting the assistant.
 */
const deleteChatSession = async (assistantId) => {
  try {
    const response = await callAPI(
      `${APP_BASE_API_URL}/chatsessions/${assistantId}`,
      'DELETE',
    );
    return {
      data: response?.payload?.data,
      TotalCount: response?.totalCount,
      isSuccess: response?.isSuccess,
      message: response?.message,
    };
  } catch (error) {
    throw new Error(`Error deleting assistant: ${error.message}`);
  }
};

export {
  listChatSessions,
  fetchChatSession,
  createChatSession,
  updateChatSession,
  deleteChatSession,
};
